import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, LockIcon, CreditCardIcon, ShieldCheckIcon, ZapIcon, FileTextIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { Reveal } from './Reveal';
import { useTodayLabel } from '../hooks/useTodayLabel';
import { useCountdown } from '../hooks/useCountdown';
import { offerStack, TOTAL_VALUE, REGULAR_PRICE, TODAY_PRICE, DISCOUNT_PERCENT } from '../data/offerStack';
const money = (value: number) => `$${value.toLocaleString('es-ES', {
  minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  maximumFractionDigits: 2
})}`;
const included = ['Los 7 capítulos completos (PDF descargable)', 'Checklist de 7 días (imprimible)', 'Acceso instantáneo, sin esperas', 'Garantía de 60 días con devolución total'];
export function OfferSection() {
  const today = useTodayLabel();
  const reduceMotion = useReducedMotion();
  const {
    hours,
    minutes,
    seconds,
    expired
  } = useCountdown(12);
  return <section id="oferta" className="w-full scroll-mt-10 bg-sand/60 py-16 sm:py-24" aria-labelledby="oferta-title" data-id="element-213" data-mp="12o1ucztcaw6g">
      <div className="mx-auto max-w-3xl px-5 sm:px-8" data-id="element-214" data-mp="xwjwikmm5bpp">
        <div className="flex justify-center" data-id="element-215" data-mp="xuywifhj50kg">
          <motion.p className="inline-flex items-center gap-2.5 rounded-full bg-clay px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cream sm:text-sm" animate={reduceMotion ? undefined : {
          boxShadow: ['0 0 0 0 rgba(180,85,60,0.45)', '0 0 0 14px rgba(180,85,60,0)']
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-216" data-mp="syqp5o9kqbnu">
            <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-217" data-mp="1ckmxfflstx7n">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cream/70" data-id="element-218" data-mp="17n4o3o3vdfz4" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-cream" data-id="element-219" data-mp="16szoequncgq" />
            </span>
            Descuento activo solo hoy, {today}
          </motion.p>
        </div>

        <Reveal data-id="element-220" data-mp="3tu98r5x759h8">
          <h2 id="oferta-title" className="mt-6 text-center font-display text-3xl font-semibold leading-tight tracking-tight text-bark sm:text-[2.6rem]" data-id="element-221" data-mp="3ttq2imgws9j1">
            Todo lo que recibes hoy
          </h2>
        </Reveal>

        <Reveal data-id="element-222" data-mp="3ksjhc03beajg">
          <img src="https://cdn.magicpatterns.com/uploads/dpPwDsYjA9MKgeko8FLieC/mokupcodigocortiso.png" alt="Código Cortisol: protocolo de 7 días, mostrado en una tablet" className="mx-auto mt-10 w-full max-w-sm rounded-2xl shadow-lift" loading="lazy" data-id="element-223" data-mp="3kt1ohrrim965" />
        </Reveal>

        <Reveal data-id="element-224" data-mp="3fv9rwkfxdyvm">
          <div className="mt-10 overflow-hidden rounded-3xl border border-bark/10 bg-cream shadow-lift" data-id="element-225" data-mp="3fvt55numrz3b">
            <ul className="divide-y divide-bark/10 px-6 sm:px-8" data-id="element-226" data-mp="31758fe91tjbs">
              {offerStack.map(item => <li key={item.name} className="flex items-start gap-4 py-5" data-id="element-227" data-mp="364yca0mvq18w">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sageDeep/10 text-sageDeep" data-id="element-228" data-mp="2s5ozlaxvtp9o">
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-229" data-mp="2x5tfozuvohzd" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-230" data-mp="2dk1e9mfy0ep8">
                    <h3 className="font-semibold leading-snug text-bark" data-id="element-231" data-mp="28jncgm4xq0zn">{item.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-ink/70" data-id="element-232" data-mp="2mj502y6hv87f">{item.description}</p>
                  </div>
                  <span className="shrink-0 pt-0.5 text-sm font-semibold text-ink/50 line-through" data-id="element-233" data-mp="2ig8ldik2rw8r">
                    {money(item.value)}
                  </span>
                </li>)}
            </ul>

            <div className="border-t border-bark/10 bg-sand/60 px-6 py-6 sm:px-8" data-id="element-234" data-mp="2rgwz0c5k3ul4">
              <div className="flex items-baseline justify-between gap-4" data-id="element-235" data-mp="366vyci8ulc26">
                <span className="text-sm font-semibold uppercase tracking-wide text-ink/70" data-id="element-236" data-mp="317rorqi0hcbb">
                  Valor total real
                </span>
                <span className="font-display text-2xl text-ink/50 line-through" data-id="element-237" data-mp="3f7tz8rnzp3gj">
                  {money(TOTAL_VALUE)}
                </span>
              </div>
              <div className="mt-3 flex items-baseline justify-between gap-4" data-id="element-238" data-mp="18jtpjbemoz1z">
                <span className="text-sm font-semibold uppercase tracking-wide text-ink/70" data-id="element-239" data-mp="1dhv87v856wcx">
                  Precio regular
                </span>
                <span className="font-display text-xl text-ink/50 line-through" data-id="element-240" data-mp="1i9d9kke2uyyf">
                  {money(REGULAR_PRICE)}
                </span>
              </div>
            </div>

            <div className="px-6 py-8 text-center sm:px-8" data-id="element-241" data-mp="pl7m3pvn2vuw">
              {!expired && <div className="mx-auto mb-7 max-w-xs" data-id="element-242" data-mp="uizhwuzen24d">
                  <p className="text-xs font-semibold uppercase tracking-widest text-ink/60" data-id="element-243" data-mp="ujix43xo7cep">
                    La oferta expira en
                  </p>
                  <div className="mt-3 flex items-start justify-center gap-2" data-id="element-244" data-mp="13k7aqxj5jar2">
                    {[{
                  value: hours,
                  label: 'horas'
                }, {
                  value: minutes,
                  label: 'min'
                }, {
                  value: seconds,
                  label: 'seg'
                }].map(unit => <div key={unit.label} className="w-20 rounded-xl bg-bark px-2 py-3" data-id="element-245" data-mp="2p559spb7wxzk">
                        <p className="font-display text-2xl font-semibold tabular-nums text-cream" data-id="element-246" data-mp="2k614i9rezkei">
                          {unit.value}
                        </p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-widest text-cream/60" data-id="element-247" data-mp="2bal04yt19muo">
                          {unit.label}
                        </p>
                      </div>)}
                  </div>
                </div>}

              <p className="text-sm font-semibold uppercase tracking-widest text-clay" data-id="element-248" data-mp="21fjfaf5sj6xw">
                Hoy, solo en esta página
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2" data-id="element-249" data-mp="1sdjxvlqv60uf">
                <span className="font-display text-5xl font-semibold tracking-tight text-bark sm:text-6xl" data-id="element-250" data-mp="3qso8oi1cbzqg">
                  $15,90
                </span>
                <span className="rounded-lg bg-clay px-3 py-1.5 text-sm font-bold text-cream" data-id="element-251" data-mp="3mooz6jeha16l">
                  −{DISCOUNT_PERCENT}% OFF
                </span>
              </div>
              <p className="mt-2 text-sm text-ink/70" data-id="element-252" data-mp="1ow5man12xxee">
                Pago único en USD · Ahorras {money(REGULAR_PRICE - TODAY_PRICE)} sobre el precio
                regular
              </p>

              <ul className="mx-auto mt-7 grid max-w-md gap-2.5 text-left" data-id="element-253" data-mp="1f5koh1t8cwwj">
                {included.map(item => <li key={item} className="flex items-start gap-2.5 text-sm text-ink/80" data-id="element-254" data-mp="22v5a5sgrq89i">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sageDeep" aria-hidden="true" data-id="element-255" data-mp="27sf67qynxi6b" />
                    <span data-id="element-256" data-mp="1ttfjioupndcs">{item}</span>
                  </li>)}
              </ul>

              <div className="mt-8 flex justify-center" data-id="element-257" data-mp="1yrz86x0ov38u">
                <CtaButton data-id="element-258" data-mp="ies8032dx2u8">SÍ, QUIERO EL CÓDIGO CORTISOL AHORA</CtaButton>
              </div>

              <div className="mx-auto mt-6 max-w-md space-y-3 text-sm text-ink/75" data-id="element-259" data-mp="ih4zb6u18pjy">
                <p className="flex items-start justify-center gap-2" data-id="element-260" data-mp="nbse2dz8i5lc">
                  <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink/60" aria-hidden="true" data-id="element-261" data-mp="evjnkoelsxqh" />
                  Procesado por Hotmart. Seguridad certificada SSL 256-bit.
                </p>
                <p className="flex items-start gap-2 text-center sm:text-left" data-id="element-262" data-mp="otzdwp5t42yx">
                  <CreditCardIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink/60" aria-hidden="true" data-id="element-263" data-mp="jvo76nat68kx" />
                  <span data-id="element-264" data-mp="3sazkflk285kk">
                    Tenemos disponibles múltiples métodos de pago y puedes pagar en la moneda local
                    de tu país. La conversión automática la ves en la próxima página, antes de
                    confirmar.
                  </span>
                </p>
              </div>

              <div className="mt-8 border-t border-bark/10 pt-6" data-id="element-265" data-mp="14obz4cq02j7">
                <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-ink/75" data-id="element-266" data-mp="39kzuyxkb4ogm">
                  <li className="flex items-center gap-2" data-id="element-267" data-mp="34mygkpxttdbh">
                    <ShieldCheckIcon className="h-4 w-4 text-sageDeep" aria-hidden="true" data-id="element-268" data-mp="34kc7umz78rro" />
                    Garantía 60 días
                  </li>
                  <li className="flex items-center gap-2" data-id="element-269" data-mp="2o2zqdmtc4isg">
                    <ZapIcon className="h-4 w-4 text-sageDeep" aria-hidden="true" data-id="element-270" data-mp="2t11bedabms6k" />
                    Acceso inmediato
                  </li>
                  <li className="flex items-center gap-2" data-id="element-271" data-mp="2y0w1iem9vdqk">
                    <LockIcon className="h-4 w-4 text-sageDeep" aria-hidden="true" data-id="element-272" data-mp="321zgkywhx5qq" />
                    SSL 256-bit
                  </li>
                  <li className="flex items-center gap-2" data-id="element-273" data-mp="36zht0z9q233h">
                    <FileTextIcon className="h-4 w-4 text-sageDeep" aria-hidden="true" data-id="element-274" data-mp="3brssy24gpow1" />
                    PDF descargable
                  </li>
                </ul>
                <p className="mt-5 text-sm text-ink/55" data-id="element-275" data-mp="3gsouwa5970yt">
                  Después de hoy, el precio vuelve a {money(REGULAR_PRICE)} USD.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}